import { FC, useCallback, useMemo } from 'react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL, isWeaveUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { RequestUpgradeModal } from '@frontend/self-upgrade';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Button, useModalControl } from '@frontend/design-system';
import { getCallIntelTrialTeaserTrackingIds } from '../../tracking-ids';

export interface CallIntelTrialTeaserActionProps {
  locationId: string;
  source?: string;
  trackingPrefix?: string;
  testIdPrefix?: string;
  onStartFreeTrial?: () => void;
  isAdmin?: boolean;
}

const DEFAULT_UPGRADE_CLICK_SOURCE = 'ci-insights-trial-teaser';
const DEFAULT_TEST_ID_PREFIX = 'trial-teaser';

export const CallIntelTrialTeaserAction: FC<CallIntelTrialTeaserActionProps> = ({
  locationId,
  source = DEFAULT_UPGRADE_CLICK_SOURCE,
  trackingPrefix,
  testIdPrefix = DEFAULT_TEST_ID_PREFIX,
  onStartFreeTrial,
  isAdmin: isAdminProp,
}) => {
  const { t } = useTranslation('call-intelligence');
  const { navigate: settingsNavigate } = useSettingsNavigate();

  const isAdmin = useMemo(
    () => isAdminProp ?? (isWeaveUser() || hasSchemaACL(locationId, Permission.MANAGE_ADD_ON_SUBSCRIPTIONS)),
    [isAdminProp, locationId]
  );

  const {
    openModal: openRequestUpgradeModal,
    closeModal: closeRequestUpgradeModal,
    modalProps: requestUpgradeModalProps,
  } = useModalControl();

  const selectedLocationIds = useMemo(() => [locationId], [locationId]);
  const trackingIds = useMemo(
    () => getCallIntelTrialTeaserTrackingIds(isAdmin, trackingPrefix),
    [isAdmin, trackingPrefix]
  );

  const handleStartFreeTrial = useCallback(() => {
    if (onStartFreeTrial) {
      onStartFreeTrial();
    } else {
      settingsNavigate({
        to: '/subscriptions/feature/:id',
        params: { id: 'call-intelligence' },
        search: { click_src: source },
      });
    }
  }, [onStartFreeTrial, settingsNavigate, source]);

  return (
    <>
      {isAdmin ? (
        <Button
          variant='primary'
          onClick={handleStartFreeTrial}
          trackingId={trackingIds.startFreeTrial}
          data-testid={`${testIdPrefix}-start-trial-button`}
        >
          {t('Start Free Trial')}
        </Button>
      ) : (
        <Button
          variant='primary'
          onClick={openRequestUpgradeModal}
          trackingId={trackingIds.requestTrial}
          data-testid={`${testIdPrefix}-request-trial-button`}
        >
          {t('Request Free Trial')}
        </Button>
      )}

      {!isAdmin && (
        <RequestUpgradeModal
          feature={Feature.CALL_INTELLIGENCE}
          selectedLocationIds={selectedLocationIds}
          title={t('Request Call Intelligence')}
          description={t(
            "Add a note to your admin about why you want access to Call Intelligence. We'll send an email to them to start a free trial!"
          )}
          onActionSuccess={closeRequestUpgradeModal}
          {...requestUpgradeModalProps}
        />
      )}
    </>
  );
};
